import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@Sage/hoi-poi-ui';

import { REG_EX_URL_ONLY } from 'constants/Constants';
import CrudField from 'containers/components/EntityCrud/CrudField';
import FieldWrapper from 'containers/components/Fields/FieldWrapper';
import { getError } from 'utils/Errors';
import Literal from 'components/Literal'; // FIXME Please use getLiteral here
import { getLiteral } from 'utils/getLiteral';
import IconItemRow from 'components/selectRows/IconItemRow';
import { CheckIn, Activities } from 'components/SvgIcons';
import CampaignFileList from './CampaignFileList';
import Context from 'managers/Context';
import { ServerListActions } from 'actions';

class FormDetails extends PureComponent {
    constructor(props) {
        super(props);
        this.state = this.getInitialState(props);
    }

    componentDidMount() {
        this.props.setCheckErrors(this.checkErrors);
        this.loadTypeOptions();
    }

    getInitialState = (props) => {
        return {
            data: {
                descriptionRich: props.data.descriptionRich || '',
                typeId: props.data.typeId,
                campaignDate: props.data.campaignDate,
                files: props.data.files,
                removedFiles: props.data.removedFiles || [],
                videoUrl: props.data.videoUrl,
            },
            errors: {},
            typeOptions: [],
        };
    };

    loadTypeOptions = () => {
        Context.store.dispatch(ServerListActions.getList('tblCampaignsTypes')).then((options) => {
            this.setState({ typeOptions: options || [] });
        });
    };

    // Convert typeId (number) to full option object {value, label}
    getTypeIdValue = () => {
        const { typeId } = this.state.data;
        const { typeOptions } = this.state;

        // If already an object, return as is
        if (typeId && typeof typeId === 'object') return typeId;

        // If primitive, find in options
        if (typeId && typeOptions.length > 0) {
            const found = typeOptions.find((opt) => String(opt.value) === String(typeId));
            return found || null;
        }

        return null;
    };

    checkErrors = () => {
        const { onToggle } = this.props;
        let { data } = this.state;
        let errors = { campaignDate: {} };
        let campaignDate = data.campaignDate || {};
        let campaignStartDate = campaignDate.campaignStartDate;
        let campaignEndDate = campaignDate.campaignEndDate;
        if (!data.typeId) errors.typeId = 'mandatory';
        if (!data.descriptionRich || !data.descriptionRich.text)
            errors.descriptionRich = 'mandatory';
        if (
            !errors.descriptionRich &&
            data.descriptionRich &&
            data.descriptionRich.html.length > 500
        ) {
            errors.descriptionRich = ['length-not-allowed', [500]];
        }
        if (!campaignStartDate) errors.campaignDate.campaignStartDate = 'mandatory';
        if (!campaignEndDate) errors.campaignDate.campaignEndDate = 'mandatory';

        if (campaignStartDate && campaignEndDate && campaignStartDate > campaignEndDate) {
            errors.campaignDate.campaignStartDate = 'campaign-start-date-before';
        }

        if (campaignEndDate && campaignEndDate < new Date()) {
            errors.campaignDate.campaignEndDate = 'campaign-end-date-future';
        }

        if (data && data.videoUrl && !REG_EX_URL_ONLY.test(data.videoUrl)) {
            errors.videoUrl = 'url-not-valid';
        }

        this.setState({ errors });
        const error = Object.keys(errors).length > 1 || Object.keys(errors.campaignDate).length;
        if (error) onToggle && onToggle(true);
        return error;
    };

    onChange = (field) => {
        let { onSave } = this.props;
        return (value) => {
            // SelectHoi returns object {value, label}, extract value for backend
            const finalValue =
                value && typeof value === 'object' && 'value' in value ? value.value : value;

            const newData = {
                ...this.state.data,
                [field]: finalValue,
            };
            this.setState({
                data: newData,
                errors: {
                    ...this.state.errors,
                    [field]: null,
                },
            });
            onSave && onSave(newData);
        };
    };

    onSave = () => {
        let { onSave, onToggle } = this.props;

        if (this.checkErrors()) return;
        onSave && onSave(this.state.data);
        onToggle && onToggle();
    };

    onFileRemove = (file) => {
        if (!file.id) return;
        setTimeout(() => {
            this.setState({
                data: {
                    ...this.state.data,
                    removedFiles: [...this.state.data.removedFiles, file],
                },
            });
        });
    };

    customTypeOption = (item) => {
        const title = item.descripcion || item.label;
        const subtitle = item.text || item.text_es;

        let icon = <Activities />;
        if (item.activitytypeid === '4') {
            icon = <CheckIn />;
        }

        return <IconItemRow icon={icon} title={title} subtitle={subtitle} />;
    };

    render() {
        const { schema } = this.props;
        const { data, errors } = this.state;

        let extraProps = {
            typeId: {
                customOption: this.customTypeOption,
            },
            campaignDate: {},
        };

        // only in ongoing state
        if (this.props.data.statusId === 2) {
            extraProps.campaignDate = {
                readOnly: {
                    campaignStartDate: true,
                },
            };
            extraProps.typeId = {
                ...extraProps.typeId,
                readOnly: true,
            };
        }

        return (
            <div className="campaigns-crud-form-details">
                {schema[0] &&
                    schema[0].fields
                        .filter((f) => !f.out)
                        .map((field) => {
                            let inputAttrs = field.inputAttrs || {};
                            let customAttrs = extraProps[field.id] || {};
                            let errorValue = errors[field.id];

                            // For object errors (like dateRange with sub-field errors), pass directly
                            // For string/array errors, use getError
                            let fieldError = null;
                            if (errorValue) {
                                if (
                                    typeof errorValue === 'object' &&
                                    !Array.isArray(errorValue) &&
                                    Object.keys(errorValue).length > 0
                                ) {
                                    // Object error (e.g., {campaignStartDate: 'mandatory'})
                                    fieldError = errorValue;
                                } else if (typeof errorValue === 'string') {
                                    fieldError = getError(errorValue);
                                } else if (Array.isArray(errorValue) && errorValue.length > 0) {
                                    fieldError = getError(...errorValue);
                                }
                            }

                            // For typeId, pass the resolved object value
                            const fieldValue =
                                field.id === 'typeId' ? this.getTypeIdValue() : data[field.id];

                            return (
                                <CrudField
                                    key={field.id}
                                    {...field}
                                    onChange={this.onChange(field.id)}
                                    value={fieldValue}
                                    fieldId={field.id}
                                    error={fieldError}
                                    inputAttrs={{
                                        ...inputAttrs,
                                        ...customAttrs,
                                    }}
                                    labelMode="horizontal"
                                />
                            );
                        })}

                <FieldWrapper
                    description={getLiteral('helptext_campaigns_new_campaign_attach_file')}
                    label={getLiteral('label_campaigns_new_campaign_attach_file')}
                    labelMode="horizontal"
                >
                    <CampaignFileList
                        canRemove={true}
                        value={data.files || []}
                        onRemove={this.onFileRemove}
                        onChange={this.onChange('files')}
                        withDropzone={true}
                    />
                </FieldWrapper>
                <div className="campaign-form-buttons">
                    <Button onClick={this.onSave}>
                        <Literal literal="action_campaigns_done" />
                    </Button>
                </div>
            </div>
        );
    }
}

FormDetails.propTypes = {
    schema: PropTypes.array,
    data: PropTypes.object,
    errors: PropTypes.object,
    onSave: PropTypes.func,
    onToggle: PropTypes.func,
};

export default FormDetails;
